(() => {
  const TARGET = '#2F3B27';

  const isProductPage = () =>
    document.body.classList.contains('template-product') ||
    document.documentElement.classList.contains('template-product') ||
    /^\/products(?:\/|$)/.test(location.pathname);

  const parseRgb = (value) => {
    if (!value || value === 'transparent') return null;

    const m = value.match(
      /rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)(?:\s*,\s*([0-9.]+))?\s*\)/i
    );

    if (!m) return null;

    return {
      r: Number(m[1]),
      g: Number(m[2]),
      b: Number(m[3]),
      a: m[4] === undefined ? 1 : Number(m[4])
    };
  };

  const isBlackish = (rgb) => {
    if (!rgb || rgb.a < 0.72) return false;

    // Covers pure black, #111/#151515/#1a1a1a and similar Harper dark surfaces.
    const max = Math.max(rgb.r, rgb.g, rgb.b);
    const min = Math.min(rgb.r, rgb.g, rgb.b);

    return max <= 55 && (max - min) <= 14;
  };

  const visibleRect = (el) => {
    if (!(el instanceof HTMLElement)) return null;

    const style = getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return null;

    const rect = el.getBoundingClientRect();
    if (rect.width < 30 || rect.height < 30) return null;

    return rect;
  };

  const isMediaElement = (el) =>
    el.matches('img, picture, video, canvas, svg, iframe');

  const shouldSkip = (el) => {
    if (!(el instanceof HTMLElement)) return true;
    if (isMediaElement(el)) return true;

    // Header/footer have their own styling decisions. This pass is for PDP content surfaces.
    if (el.closest('header, footer, .header, .footer, .announcement-bar')) return true;

    return false;
  };

  const isMeaningfulSurface = (el, rect) => {
    const viewportArea = Math.max(1, window.innerWidth * window.innerHeight);
    const area = rect.width * rect.height;

    const sectionLike =
      el.matches('section, article, main, [class*="section"], [class*="module"], [class*="band"], [class*="panel"]');

    const wideBand =
      rect.width >= window.innerWidth * 0.72 &&
      rect.height >= 120;

    const largeBlock =
      area >= viewportArea * 0.055;

    return sectionLike || wideBand || largeBlock;
  };

  const removeNonImageBackground = (el, style) => {
    const bgImage = style.backgroundImage || '';

    // Never strip actual image/video background URLs.
    if (bgImage && bgImage !== 'none' && !/url\(/i.test(bgImage)) {
      el.style.setProperty('background-image', 'none', 'important');
    }
  };

  const paint = (el) => {
    if (shouldSkip(el)) return;

    const rect = visibleRect(el);
    if (!rect) return;

    const style = getComputedStyle(el);
    const bg = parseRgb(style.backgroundColor);

    if (!isBlackish(bg)) return;
    if (!isMeaningfulSurface(el, rect)) return;

    el.classList.add('elizna-pdp-green-surface');
    el.style.setProperty('background-color', TARGET, 'important');
    el.style.setProperty('border-color', TARGET, 'important');

    removeNonImageBackground(el, style);

    el.dataset.eliznaPdpDarkSurfaceGreen = '1';
  };

  const scan = (scope = document) => {
    if (!isProductPage()) return;

    const main =
      document.querySelector('main') ||
      document.querySelector('[role="main"]') ||
      document.body;

    if (!main) return;

    paint(main);

    main.querySelectorAll('*').forEach(paint);
  };

  let queued = false;

  const queueScan = () => {
    if (queued) return;
    queued = true;

    requestAnimationFrame(() => {
      queued = false;
      scan();
    });
  };

  const init = () => {
    if (!isProductPage()) return;

    scan();

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.addedNodes?.length || mutation.type === 'attributes') {
          queueScan();
          break;
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });

    window.addEventListener('load', queueScan, { once: true });
    window.addEventListener('resize', queueScan, { passive: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', queueScan);
})();
